import jsPDF from 'jspdf'
import {
  ResumeSectionType,
  ContactInfo,
  contactDetailLineParts,
  contactFullName,
  WorkExperienceCompany,
  WorkExperienceRole,
  Education,
  SkillCategory,
  Certification,
  Project,
  ProfessionalSummaryEntry,
} from '@applicator/shared'

import type { ResolvedResumeColors } from '../types/resumeColorScheme'
import { loadResumeColorPreference, resolveResumeColors } from '../types/resumeColorScheme'
import {
  PDF_CONFIG,
  PT_TO_MM,
  TWO_COLUMN_COL_GAP_RATIO,
  TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM,
  TWO_COLUMN_DIVIDER_TO_ROW_MM,
  TWO_COLUMN_LEAD_BLOCK_BOTTOM_GAP_MM,
  TWO_COLUMN_LEAD_TEXT_TOP_PAD_MM,
  TWO_COLUMN_LEFT_COL_RATIO,
  TWO_COLUMN_RIGHT_COL_RATIO,
  TWO_COLUMN_SUMMARY_ONLY_TOP_GAP_MM,
  TWO_COLUMN_SUMMARY_PARAGRAPH_GAP_MM,
  TWO_COLUMN_TARGET_ITEM_GAP_MM,
} from './resumeExportPdfConfig'
import { getSelectedTargetTitle } from './targetTitleUtils'
import {
  baselineForAlignedCapTops,
  splitResumeParagraphs,
  twoColumnSectionHeaderGapsMm,
} from './resumeExportTwoColumnLayout'
import {
  formatCertificationDates,
  isCertificationDatesInPreview,
  isCertificationInPreview,
  isCertificationCredentialLinkInPreview,
  isCertificationIssuerInPreview,
  isCertificationNameInPreview,
} from './certificationPreview'
import {
  isWorkExperienceCompanyDescriptionInPreview,
  isWorkExperienceCompanyInPreview,
  isWorkExperienceRoleInPreview,
} from './workExperiencePreview'
import {
  WORK_EXPERIENCE_TEAL_HEADER_BULLET,
  workExperienceTealLine1HasContent,
  workExperienceTealLine1Parts,
  workExperienceTealLine2Parts,
} from './workExperienceRoleHeaderPreview'

import { drawResumePdfHeader } from './resumeExportPdfHeader'
import {
  drawTealInlinePartsOnPdf,
  tealLine1DrawStyle,
  tealLine2DrawStyleTwoColumn,
  type TealInlineDrawPart,
  type TealInlineDrawStyle,
} from './resumeExportTealInlineDraw'

type Column = 'left' | 'right'

const LEFT_COL_RATIO = TWO_COLUMN_LEFT_COL_RATIO
const GAP_COL_RATIO = TWO_COLUMN_COL_GAP_RATIO
const RIGHT_COL_RATIO = TWO_COLUMN_RIGHT_COL_RATIO

/**
 * Two-column PDF: left and right columns paginate independently (jsPDF setPage per column).
 */
class TwoColumnPDFGenerator {
  private pdf: jsPDF
  private leftY: number
  private rightY: number
  private leftPage = 1
  private rightPage = 1
  private readonly leftX: number
  private readonly rightX: number
  private readonly leftW: number
  private readonly rightW: number
  private readonly fullContentW: number
  /** Printable right edge (page width minus right margin). */
  private readonly contentRightX: number
  private readonly marginTop: number
  private readonly marginBottom: number
  private readonly pageW = PDF_CONFIG.pageWidth
  private readonly pageH = PDF_CONFIG.pageHeight
  private readonly accentRgb: [number, number, number]

  constructor(accentRgb?: [number, number, number]) {
    this.accentRgb = accentRgb ?? PDF_CONFIG.colors.twoColumnSection
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const ml = PDF_CONFIG.marginPt.left * PT_TO_MM
    const mr = PDF_CONFIG.marginPt.right * PT_TO_MM
    this.contentRightX = this.pageW - mr
    this.fullContentW = this.contentRightX - ml
    this.leftW = this.fullContentW * LEFT_COL_RATIO
    this.leftX = ml
    const gapW = this.fullContentW * GAP_COL_RATIO
    this.rightW = this.fullContentW * RIGHT_COL_RATIO
    this.rightX = ml + this.leftW + gapW
    this.marginTop = PDF_CONFIG.marginPt.top * PT_TO_MM
    this.marginBottom = PDF_CONFIG.marginPt.bottom * PT_TO_MM
    this.leftY = this.marginTop
    this.rightY = this.marginTop
    this.pdf.setFont('helvetica', 'normal')
  }

  private ptToMm(pt: number): number {
    return pt * PT_TO_MM
  }

  private colY(col: Column): number {
    return col === 'left' ? this.leftY : this.rightY
  }

  private setColY(col: Column, y: number): void {
    if (col === 'left') this.leftY = y
    else this.rightY = y
  }

  private colX(col: Column): number {
    return col === 'left' ? this.leftX : this.rightX
  }

  private colW(col: Column): number {
    return col === 'left' ? this.leftW : this.rightW
  }

  private colPage(col: Column): number {
    return col === 'left' ? this.leftPage : this.rightPage
  }

  private setColPage(col: Column, page: number): void {
    if (col === 'left') this.leftPage = page
    else this.rightPage = page
  }

  private ensurePageExists(pageNum: number): void {
    while (this.pdf.getNumberOfPages() < pageNum) {
      this.pdf.addPage()
    }
  }

  /** Activate PDF page for this column before any draw call. */
  private activateColumn(col: Column): void {
    const page = this.colPage(col)
    this.ensurePageExists(page)
    this.pdf.setPage(page)
  }

  private checkPageBreakColumn(col: Column, requiredHeight: number): void {
    if (this.colY(col) + requiredHeight <= this.pageH - this.marginBottom) return
    const next = this.colPage(col) + 1
    this.setColPage(col, next)
    this.ensurePageExists(next)
    this.setColY(col, this.marginTop)
  }

  /** Full-width blocks (header): both columns stay on the same page. */
  private checkPageBreakFullWidth(requiredHeight: number): void {
    const y = Math.max(this.leftY, this.rightY)
    if (y + requiredHeight <= this.pageH - this.marginBottom) return
    const next = Math.max(this.leftPage, this.rightPage) + 1
    this.leftPage = next
    this.rightPage = next
    this.ensurePageExists(next)
    this.leftY = this.marginTop
    this.rightY = this.marginTop
  }

  private addSpace(col: Column, mm: number): void {
    this.setColY(col, this.colY(col) + mm)
  }

  private normalizeText(text: string): string {
    const sep = WORK_EXPERIENCE_TEAL_HEADER_BULLET
    const placeholder = '\uE000'
    return String(text || '')
      .replace(/\u00ad/g, '')
      .replace(/\u200b/g, '')
      .split(sep)
      .join(placeholder)
      .replace(/[\u2022\u25CF\uF0B7]/g, ' ')
      .split(placeholder)
      .join(sep)
      .replace(/\s+/g, ' ')
      .trim()
  }

  /** Allow jsPDF to wrap long URLs inside the right (narrow) column. */
  private breakLongTokens(text: string): string {
    return String(text).replace(/(https?:\/\/[^\s]+)/g, (url) =>
      url.replace(/([-/?#&=._%A-Za-z0-9]{22})/g, '$1\u200b')
    )
  }

  private setTextColorRgb(rgb: [number, number, number]): void {
    this.pdf.setTextColor(rgb[0], rgb[1], rgb[2])
  }

  private colInnerWidth(col: Column, indent = 0): number {
    return Math.max(0, Math.min(this.colW(col), this.contentRightX - this.colX(col)) - indent)
  }

  private addColBoldWrapped(col: Column, text: string): void {
    const normalized = this.breakLongTokens(this.normalizeText(text))
    if (!normalized) return
    const maxW = this.colInnerWidth(col)
    const lineH = this.ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body
    const x = this.colX(col)
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.setFontSize(PDF_CONFIG.fontSize.body)
    this.setTextColorRgb(PDF_CONFIG.colors.black)
    const lines = this.pdf.splitTextToSize(normalized, maxW)
    for (const line of lines) {
      this.checkPageBreakColumn(col, lineH)
      this.activateColumn(col)
      this.pdf.text(line, x, this.colY(col))
      this.setColY(col, this.colY(col) + lineH)
    }
  }

  private addSectionHeader(
    col: Column,
    title: string,
    options?: { skipMarginTop?: boolean }
  ): void {
    const cfg = PDF_CONFIG.sectionHeader
    const fontSize = PDF_CONFIG.fontSize.sectionHeader
    const { titleToLineMm, ruleThicknessMm, lineToBodyMm } = twoColumnSectionHeaderGapsMm()
    const marginTopMm = options?.skipMarginTop ? 0 : this.ptToMm(cfg.marginTop)
    const totalH = marginTopMm + titleToLineMm + ruleThicknessMm + lineToBodyMm

    if (marginTopMm > 0) {
      this.addSpace(col, marginTopMm)
    }
    this.checkPageBreakColumn(col, totalH)
    this.activateColumn(col)

    this.pdf.setFont('helvetica', 'normal')
    this.pdf.setFontSize(fontSize)
    this.setTextColorRgb(this.accentRgb)
    const x = this.colX(col)
    let y = this.colY(col)
    this.pdf.text(title.toUpperCase(), x, y)
    y += titleToLineMm
    this.pdf.setDrawColor(0, 0, 0)
    this.pdf.setLineWidth(ruleThicknessMm)
    this.pdf.line(x, y, Math.min(x + this.colW(col), this.contentRightX), y)
    y += ruleThicknessMm + lineToBodyMm
    this.setColY(col, y)
    this.setTextColorRgb(PDF_CONFIG.colors.black)
  }

  /** Body text with blank-line paragraph breaks (preview `whitespace-pre-wrap`). */
  private addColParagraphs(col: Column, text: string, paragraphGapMm: number, indent = 0): void {
    const paragraphs = splitResumeParagraphs(text)
    for (let i = 0; i < paragraphs.length; i++) {
      if (i > 0) this.addSpace(col, paragraphGapMm)
      this.addColParagraph(col, paragraphs[i]!, indent)
    }
  }

  private addColParagraph(col: Column, text: string, indent = 0): void {
    const normalized = this.normalizeText(text)
    if (!normalized) return
    this.pdf.setFont('helvetica', 'normal')
    this.pdf.setFontSize(PDF_CONFIG.fontSize.body)
    this.pdf.setTextColor(0, 0, 0)
    const maxW = this.colInnerWidth(col, indent)
    const lines = this.pdf.splitTextToSize(this.breakLongTokens(normalized), maxW)
    const lineH = this.ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body
    const x = this.colX(col) + indent
    for (const line of lines) {
      this.checkPageBreakColumn(col, lineH)
      this.activateColumn(col)
      this.pdf.text(line, x, this.colY(col))
      this.setColY(col, this.colY(col) + lineH)
    }
  }

  private addColBullet(col: Column, text: string): void {
    const normalized = this.normalizeText(text)
    if (!normalized) return
    const indent = PDF_CONFIG.spacing.bulletIndent
    const bulletX = this.colX(col) + indent - 3
    const textX = this.colX(col) + indent
    const maxW = Math.min(this.colW(col), this.contentRightX - this.colX(col)) - indent
    this.pdf.setFont('helvetica', 'normal')
    this.pdf.setFontSize(PDF_CONFIG.fontSize.bullet)
    const lines = this.pdf.splitTextToSize(normalized, maxW)
    const lineH = this.ptToMm(PDF_CONFIG.fontSize.bullet) * PDF_CONFIG.lineHeight.bullet
    this.checkPageBreakColumn(col, lineH * lines.length)
    this.activateColumn(col)
    this.pdf.text('•', bulletX, this.colY(col))
    this.pdf.text(lines[0], textX, this.colY(col))
    this.setColY(col, this.colY(col) + lineH)
    for (let i = 1; i < lines.length; i++) {
      this.checkPageBreakColumn(col, lineH)
      this.activateColumn(col)
      this.pdf.text(lines[i], textX, this.colY(col))
      this.setColY(col, this.colY(col) + lineH)
    }
  }

  /** Flowing Teal header segments with • separators and column wrap. */
  private drawTealInlineParts(
    col: Column,
    parts: ReadonlyArray<TealInlineDrawPart>,
    style: TealInlineDrawStyle
  ): void {
    drawTealInlinePartsOnPdf(
      this.pdf,
      {
        leftX: () => this.colX(col),
        maxX: Math.min(this.colX(col) + this.colW(col), this.contentRightX),
        getY: () => this.colY(col),
        setY: (y) => this.setColY(col, y),
        checkPageBreak: (lineH) => this.checkPageBreakColumn(col, lineH),
        onBeforeLine: () => this.activateColumn(col),
      },
      parts,
      style,
      PDF_CONFIG.colors.black
    )
  }

  /** Teal line 1: Company • Location • Dates — all bold; company pt slightly larger than meta. */
  private addTealRoleLine1(
    col: Column,
    parts: ReturnType<typeof workExperienceTealLine1Parts>
  ): void {
    if (!parts.length) return
    this.drawTealInlineParts(col, parts, tealLine1DrawStyle((pt) => this.ptToMm(pt)))
  }

  /** Teal line 2: Position • Employment • Dates — position bold; meta normal. */
  private addTealRoleLine2(
    col: Column,
    parts: ReturnType<typeof workExperienceTealLine2Parts>
  ): void {
    if (!parts.length) return
    this.drawTealInlineParts(col, parts, tealLine2DrawStyleTwoColumn((pt) => this.ptToMm(pt)))
  }

  save(filename: string): void {
    this.pdf.save(filename)
  }

  renderHeader(fullName: string, contactParts: string[]): void {
    drawResumePdfHeader(
      {
        pdf: this.pdf,
        leftX: this.leftX,
        fullContentW: this.fullContentW,
        getY: () => Math.max(this.leftY, this.rightY),
        setY: (y) => {
          this.leftY = y
          this.rightY = y
        },
        checkPageBreak: (h) => this.checkPageBreakFullWidth(h),
        ptToMm: (pt) => this.ptToMm(pt),
        normalizeText: (t) => this.normalizeText(t),
        beforeDraw: () => this.activateColumn('left'),
      },
      {
        fullName,
        contactParts,
        nameColor: this.accentRgb,
        afterDividerGapMm: TWO_COLUMN_DIVIDER_TO_ROW_MM,
      }
    )
    this.leftPage = Math.max(this.leftPage, this.rightPage)
    this.rightPage = this.leftPage
  }

  /** Y/page where left lead (title/summary) began — right column aligns here like preview. */
  private leftLeadStartY = 0
  private leftLeadStartPage = 1

  alignColumnsToLeftLeadStart(): void {
    this.rightPage = this.leftLeadStartPage
    this.rightY = baselineForAlignedCapTops(
      this.leftLeadStartY,
      PDF_CONFIG.fontSize.jobTitle,
      PDF_CONFIG.fontSize.sectionHeader
    )
  }

  renderLeftColumnLead(targetTitles: string[], summaries: ProfessionalSummaryEntry[]): void {
    const col: Column = 'left'
    if (targetTitles.length > 0 || summaries.length > 0) {
      this.addSpace(col, TWO_COLUMN_LEAD_TEXT_TOP_PAD_MM)
    }
    this.leftLeadStartY = this.colY(col)
    this.leftLeadStartPage = this.colPage(col)
    for (let t = 0; t < targetTitles.length; t++) {
      const title = targetTitles[t]!
      const lineH = this.ptToMm(PDF_CONFIG.fontSize.jobTitle) * PDF_CONFIG.lineHeight.jobTitle
      this.pdf.setFont('helvetica', 'bold')
      this.pdf.setFontSize(PDF_CONFIG.fontSize.jobTitle)
      this.pdf.setTextColor(0, 0, 0)
      const lines = this.pdf.splitTextToSize(this.breakLongTokens(this.normalizeText(title)), this.colInnerWidth(col))
      for (const line of lines) {
        this.checkPageBreakColumn(col, lineH)
        this.activateColumn(col)
        this.pdf.text(line, this.colX(col), this.colY(col))
        this.setColY(col, this.colY(col) + lineH)
      }
      if (t < targetTitles.length - 1) {
        this.addSpace(col, TWO_COLUMN_TARGET_ITEM_GAP_MM)
      }
    }
    if (targetTitles.length > 0 && summaries.length > 0) {
      this.addSpace(col, TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM)
    } else if (targetTitles.length === 0 && summaries.length > 0) {
      this.addSpace(col, TWO_COLUMN_SUMMARY_ONLY_TOP_GAP_MM)
    }
    for (let i = 0; i < summaries.length; i++) {
      if (i > 0) this.addSpace(col, TWO_COLUMN_SUMMARY_PARAGRAPH_GAP_MM)
      this.addColParagraphs(col, summaries[i]!.text, TWO_COLUMN_SUMMARY_PARAGRAPH_GAP_MM)
    }
    if (targetTitles.length > 0 || summaries.length > 0) {
      this.addSpace(col, TWO_COLUMN_LEAD_BLOCK_BOTTOM_GAP_MM)
    }
  }

  renderWorkExperience(companies: WorkExperienceCompany[]): void {
    const col: Column = 'left'
    if (companies.length === 0) return
    this.addSectionHeader(col, 'Work Experience')
    for (const company of companies) {
      const roles = (company.roles || []).filter((r: WorkExperienceRole) =>
        isWorkExperienceRoleInPreview(company, r)
      )
      for (const role of roles as WorkExperienceRole[]) {
        const line1Parts = workExperienceTealLine1Parts(company, role as WorkExperienceRole)
        if (workExperienceTealLine1HasContent(line1Parts)) {
          this.addTealRoleLine1(col, line1Parts)
        }
        if (
          isWorkExperienceCompanyDescriptionInPreview(
            company as WorkExperienceCompany,
            role as WorkExperienceRole
          )
        ) {
          this.addSpace(col, 1)
          this.addColParagraph(col, company.description!)
        }
        const line2Parts = workExperienceTealLine2Parts(role as WorkExperienceRole)
        if (line2Parts.length > 0) {
          this.addTealRoleLine2(col, line2Parts)
        }
        const bullets = (role.bulletPoints || []).filter((bp) => bp.included)
        if (bullets.length > 0) {
          this.addSpace(col, 1)
          for (const bp of bullets) {
            this.addColBullet(col, bp.text)
          }
        }
        this.addSpace(col, PDF_CONFIG.spacing.itemGap)
      }
    }
  }

  renderSkills(categories: SkillCategory[]): void {
    const col: Column = 'right'
    const active = categories.filter((cat) => cat.skills.some((s) => s.included))
    if (active.length === 0) return
    this.addSectionHeader(col, 'Skills', { skipMarginTop: true })
    for (const category of active) {
      const skills = category.skills
        .filter((s) => s.included)
        .map((s) => s.name)
        .join(', ')
      if (!skills) continue
      this.addColBoldWrapped(col, `${category.name}:`)
      this.addColParagraph(col, skills)
      this.addSpace(col, 1)
    }
  }

  renderEducation(educations: Education[]): void {
    const col: Column = 'right'
    if (educations.length === 0) return
    this.addSectionHeader(col, 'Education')
    for (const edu of educations) {
      if ((edu.degreeIncluded ?? false) && edu.degree) {
        const degreeText = edu.fieldOfStudy ? `${edu.degree} in ${edu.fieldOfStudy}` : edu.degree
        this.addColBoldWrapped(col, degreeText)
      }
      if (edu.institution) {
        this.addColParagraph(col, edu.institution, 0)
      }
      const startDate = (edu.startDateIncluded ?? false) && edu.startDate ? edu.startDate : ''
      const endDate = (edu.endDateIncluded ?? false) && edu.endDate ? edu.endDate : ''
      const dates = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate
      if (dates) {
        this.addColParagraph(col, dates)
      }
      this.addSpace(col, PDF_CONFIG.spacing.itemGap)
    }
  }

  renderCertifications(certs: Certification[]): void {
    const col: Column = 'right'
    if (certs.length === 0) return
    this.addSectionHeader(col, 'Certifications')
    for (const cert of certs) {
      if (isCertificationNameInPreview(cert)) {
        this.addColBoldWrapped(col, cert.name)
      }
      if (isCertificationDatesInPreview(cert)) {
        this.addColParagraph(col, formatCertificationDates(cert))
      }
      if (isCertificationIssuerInPreview(cert)) {
        this.addColParagraph(col, cert.issuer!)
      }
      const url = cert.credentialUrl?.trim() ?? ''
      if (
        url &&
        isCertificationCredentialLinkInPreview(cert) &&
        url !== cert.name?.trim()
      ) {
        this.addColParagraph(col, url)
      }
      this.addSpace(col, PDF_CONFIG.spacing.itemGap)
    }
  }

  renderProjects(projects: Project[]): void {
    const col: Column = 'left'
    if (projects.length === 0) return
    this.addSectionHeader(col, 'Projects')
    for (const project of projects) {
      this.addColBoldWrapped(col, project.name)
      if (project.company) this.addColParagraph(col, project.company)
      const bullets = (project.bulletPoints || []).filter((bp) => bp.included)
      for (const bp of bullets) {
        this.addColBullet(col, bp.text)
      }
      this.addSpace(col, PDF_CONFIG.spacing.itemGap)
    }
  }

  renderInterests(names: string[]): void {
    const col: Column = 'right'
    if (names.length === 0) return
    this.addSectionHeader(col, 'Interests')
    this.addColParagraph(col, names.join(', '))
  }
}

export async function exportResumeToPDFTwoColumn(
  data: Record<ResumeSectionType, Record<string, unknown>>,
  filename: string,
  colors?: ResolvedResumeColors
): Promise<void> {
  const resolvedColors = colors ?? resolveResumeColors(loadResumeColorPreference())
  const pdf = new TwoColumnPDFGenerator(resolvedColors.accentRgb)
  const contactInfo = data.contact_info as ContactInfo
  const fullName = contactFullName(contactInfo) || 'Your Name'
  const contactParts = contactDetailLineParts(contactInfo)
  const selectedTargetTitle = getSelectedTargetTitle(data.target_title)
  const targetTitles = selectedTargetTitle ? [selectedTargetTitle] : []
  const summaries =
    data.professional_summary && Array.isArray(data.professional_summary)
      ? (data.professional_summary as ProfessionalSummaryEntry[]).filter((e) => e.included)
      : []

  pdf.renderHeader(fullName, contactParts)
  pdf.renderLeftColumnLead(targetTitles, summaries)

  // Right column (Skills, …) starts at the same Y as the left lead block, like the preview table row.
  pdf.alignColumnsToLeftLeadStart()

  if (data.work_experience && Array.isArray(data.work_experience)) {
    const companies = (data.work_experience as WorkExperienceCompany[]).filter((c) =>
      isWorkExperienceCompanyInPreview(c)
    )
    pdf.renderWorkExperience(companies)
  }

  if (data.skills && Array.isArray(data.skills)) {
    pdf.renderSkills(data.skills as SkillCategory[])
  }

  if (data.education && Array.isArray(data.education)) {
    const educations = (data.education as Education[]).filter((e) => e.included ?? false)
    pdf.renderEducation(educations)
  }

  if (data.certifications && Array.isArray(data.certifications)) {
    const certs = (data.certifications as Certification[]).filter(isCertificationInPreview)
    pdf.renderCertifications(certs)
  }

  if (data.projects && Array.isArray(data.projects)) {
    const projects = (data.projects as Project[]).filter((p) => p.included)
    pdf.renderProjects(projects)
  }

  if (data.interests && Array.isArray(data.interests)) {
    const names = (data.interests as Array<{ name: string; included?: boolean }>)
      .filter((i) => i.included !== false)
      .map((i) => i.name)
    pdf.renderInterests(names)
  }

  pdf.save(filename)
}
